/**
 * Time, shaped: the easing curves and durations of tokens.css, springs for
 * the things that follow a hand, and the few timing formulas of the call →
 * effect grammar (flight time, bit stagger, backlog speed).
 *
 * Pure. The conductor and camera read their clocks from outside, so every
 * curve here is a function of elapsed milliseconds and nothing else.
 */

import type { Theme } from './palette';

export type Ease = (t: number) => number;

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** CSS cubic-bezier(x1, y1, x2, y2): y as a function of x, by Newton steps with a bisection fallback. */
export const cubicBezier = (x1: number, y1: number, x2: number, y2: number): Ease => {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sx = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sy = (t: number) => ((ay * t + by) * t + cy) * t;
  const dx = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  const solve = (x: number): number => {
    let t = x;
    for (let i = 0; i < 8; i++) {
      const e = sx(t) - x;
      if (Math.abs(e) < 1e-7) return t;
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= e / d;
    }
    let lo = 0, hi = 1;
    t = x;
    for (let i = 0; i < 40; i++) {
      const v = sx(t);
      if (Math.abs(v - x) < 1e-7) return t;
      if (v < x) lo = t; else hi = t;
      t = (lo + hi) / 2;
    }
    return t;
  };
  return (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : sy(solve(x)));
};

export const linear: Ease = (t) => clamp(t, 0, 1);

/** `cubic-bezier(.2,0,0,1)`, `linear`, or a CSS keyword; null when unreadable. */
export const parseEase = (text: string): Ease | null => {
  const t = text.trim().toLowerCase();
  const m = /^cubic-bezier\(\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*\)$/.exec(t);
  if (m) {
    const [a, b, c, d] = [m[1], m[2], m[3], m[4]].map(Number) as [number, number, number, number];
    if ([a, b, c, d].every(Number.isFinite) && a >= 0 && a <= 1 && c >= 0 && c <= 1) return cubicBezier(a, b, c, d);
    return null;
  }
  if (t === 'linear') return linear;
  if (t === 'ease') return cubicBezier(0.25, 0.1, 0.25, 1);
  if (t === 'ease-in') return cubicBezier(0.42, 0, 1, 1);
  if (t === 'ease-out') return cubicBezier(0, 0, 0.58, 1);
  if (t === 'ease-in-out') return cubicBezier(0.42, 0, 0.58, 1);
  return null;
};

/** `160ms` or `.16s` → milliseconds; null when unreadable. */
export const parseMs = (text: string): number | null => {
  const m = /^\s*([\d.]+)\s*(ms|s)\s*$/i.exec(text);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  return (m[2] as string).toLowerCase() === 's' ? n * 1000 : n;
};

/** The motion tokens, parsed. */
export interface Motion {
  readonly press: number; readonly quick: number; readonly travel: number; readonly reveal: number; readonly dive: number;
  readonly staggerBit: number; readonly staggerCap: number;
  readonly std: Ease; readonly out: Ease; readonly io: Ease; readonly diveEase: Ease;
}

export const DEFAULT_MOTION: Motion = {
  press: 90, quick: 160, travel: 420, reveal: 640, dive: 900, staggerBit: 14, staggerCap: 220,
  std: cubicBezier(0.2, 0, 0, 1), out: cubicBezier(0.16, 1, 0.3, 1),
  io: cubicBezier(0.65, 0, 0.35, 1), diveEase: cubicBezier(0.7, 0, 0.2, 1),
};

/**
 * The motion of a theme. Durations are the full-motion values even when the
 * stylesheet has shortened them for reduced motion: the conductor applies the
 * reduced mapping itself, so it must not apply it twice.
 */
export const motionOf = (theme: Pick<Theme, 'motion'> | null): Motion => {
  if (!theme) return DEFAULT_MOTION;
  const ms = (k: string, d: number) => {
    const v = parseMs(theme.motion[k] ?? '');
    return v === null ? d : v;
  };
  const ease = (k: string, d: Ease) => parseEase(theme.motion[k] ?? '') ?? d;
  const travel = ms('--t-travel', 420);
  const reduced = travel <= 150;   // the stylesheet's reduced block is on
  return {
    press: ms('--t-press', 90), quick: ms('--t-quick', 160),
    travel: reduced ? DEFAULT_MOTION.travel : travel,
    reveal: reduced ? DEFAULT_MOTION.reveal : ms('--t-reveal', 640),
    dive: reduced ? DEFAULT_MOTION.dive : ms('--t-dive', 900),
    staggerBit: reduced ? DEFAULT_MOTION.staggerBit : ms('--stagger-bit', 14),
    staggerCap: reduced ? DEFAULT_MOTION.staggerCap : ms('--stagger-cap', 220),
    std: ease('--ease-std', DEFAULT_MOTION.std), out: ease('--ease-out', DEFAULT_MOTION.out),
    io: ease('--ease-io', DEFAULT_MOTION.io), diveEase: ease('--ease-dive', DEFAULT_MOTION.diveEase),
  };
};

// ---------- the grammar's timings ----------

/** Beat 2: `clamp(260, 180 + 0.45·d, 720)` ms for a flight of d CSS px. */
export const flightMs = (distance: number): number => clamp(180 + 0.45 * Math.max(0, distance), 260, 720);

/** Beat 5: a bit's ignition delay, by its Manhattan distance (in bits) from the impact. */
export const staggerMs = (distance: number, motion: Motion = DEFAULT_MOTION): number =>
  Math.min(Math.max(0, distance) * motion.staggerBit, motion.staggerCap);

/**
 * How fast a backlog of queued commits plays: 1× up to 8, then 2× rising to
 * 4× by 40; above 40 the conductor snaps instead of playing.
 */
export const backlogSpeed = (queued: number): number => (queued <= 8 ? 1 : clamp(2 + ((queued - 8) * 2) / 32, 2, 4));
export const SNAP_BACKLOG = 40;

/** Progress of a timed step: 0 before `t0`, 1 after `t0 + dur`. */
export const progress = (now: number, t0: number, dur: number): number => (dur <= 0 ? (now >= t0 ? 1 : 0) : clamp((now - t0) / dur, 0, 1));

/** A point on the quadratic arc a token flies: from `a` to `b`, bowed sideways by `bow` of the distance. */
export const arcPoint = (ax: number, ay: number, bx: number, by: number, t: number, bow = 0.22): { x: number; y: number } => {
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  const dx = bx - ax, dy = by - ay;
  // bow up (screen y negative) whichever way the flight goes
  const side = dx >= 0 ? -1 : 1;
  const cx = mx + side * dy * bow, cy = my - Math.abs(dx) * bow - Math.abs(dy) * bow * 0.2;
  const u = 1 - t;
  return { x: u * u * ax + 2 * u * t * cx + t * t * bx, y: u * u * ay + 2 * u * t * cy + t * t * by };
};

// ---------- springs ----------

export interface SpringParams { readonly k: number; readonly c: number; readonly m?: number }

/** The springs of §4.3. */
export const SPRINGS = {
  card: { k: 420, c: 32 },        // a lifted card's tilt and scale
  ghost: { k: 320, c: 26 },       // the carried ghost following the hand
  snapHome: { k: 520, c: 30 },    // a refused drop springing home
} as const satisfies Record<string, SpringParams>;

export interface SpringState { x: number; v: number }

/**
 * Advance a damped spring toward `target` by `dtMs`, in sub-steps of at most
 * 4 ms (semi-implicit Euler is stable there for these stiffnesses).
 */
export const springStep = (s: SpringState, target: number, p: SpringParams, dtMs: number): SpringState => {
  const m = p.m ?? 1;
  let x = s.x, v = s.v;
  let left = Math.max(0, dtMs) / 1000;
  while (left > 0) {
    const h = Math.min(left, 0.004);
    const a = (-p.k * (x - target) - p.c * v) / m;
    v += a * h;
    x += v * h;
    left -= h;
  }
  return { x, v };
};

export const springSettled = (s: SpringState, target: number, eps = 0.01): boolean =>
  Math.abs(s.x - target) < eps && Math.abs(s.v) < eps * 10;

/** A 2D spring, for a ghost following a pointer. */
export class Spring2 {
  x: SpringState;
  y: SpringState;
  constructor(x: number, y: number, public params: SpringParams = SPRINGS.ghost) {
    this.x = { x, v: 0 };
    this.y = { x: y, v: 0 };
  }
  step(tx: number, ty: number, dtMs: number): { x: number; y: number } {
    this.x = springStep(this.x, tx, this.params, dtMs);
    this.y = springStep(this.y, ty, this.params, dtMs);
    return { x: this.x.x, y: this.y.x };
  }
  jump(x: number, y: number): void {
    this.x = { x, v: 0 };
    this.y = { x: y, v: 0 };
  }
  settled(tx: number, ty: number, eps = 0.25): boolean {
    return springSettled(this.x, tx, eps) && springSettled(this.y, ty, eps);
  }
}

/** A value tweened between two numbers on a clock. */
export class Tween {
  constructor(public from: number, public to: number, public t0: number, public dur: number, public ease: Ease = DEFAULT_MOTION.out) {}
  value(now: number): number {
    return lerp(this.from, this.to, this.ease(progress(now, this.t0, this.dur)));
  }
  done(now: number): boolean {
    return now >= this.t0 + this.dur;
  }
}

// ---------- magnetic cards ----------

export const CARD_TILT_MAX = 6;   // degrees

/**
 * A card's lean toward the pointer: at most 6° on each axis, zero at the
 * centre, and the pointer's place as percentages for the conic sheen.
 */
export const magnetTilt = (px: number, py: number, rect: { left: number; top: number; width: number; height: number }, max = CARD_TILT_MAX): { rx: number; ry: number; mx: number; my: number } => {
  const u = clamp((px - rect.left) / Math.max(1, rect.width), 0, 1);
  const v = clamp((py - rect.top) / Math.max(1, rect.height), 0, 1);
  return { rx: (0.5 - v) * 2 * max, ry: (u - 0.5) * 2 * max, mx: u * 100, my: v * 100 };
};

/** A lifted card leans with its velocity: px/s → degrees, capped. */
export const velocityLean = (vx: number, max = 8): number => clamp(vx * 0.012, -max, max);

/** How big a lifted card grows (§4.3). */
export const LIFT_SCALE = 1.04;

/**
 * A lifted card's pose (§4.3): it grows to 1.04 and leans with its velocity,
 * both on the card spring (k 420, c 32), so a flick swings it and a stop
 * lets it settle upright. Where it is comes from the ghost's own spring.
 */
export class CardLift {
  lean: SpringState = { x: 0, v: 0 };
  scale: SpringState = { x: 1, v: 0 };
  step(vx: number, dtMs: number): { lean: number; scale: number } {
    this.lean = springStep(this.lean, velocityLean(vx), SPRINGS.card, dtMs);
    this.scale = springStep(this.scale, LIFT_SCALE, SPRINGS.card, dtMs);
    return { lean: this.lean.x, scale: this.scale.x };
  }
  settled(): boolean {
    return springSettled(this.lean, 0, 0.02) && springSettled(this.scale, LIFT_SCALE, 0.001);
  }
  /** At rest, lifted (reduced motion: no swing, the size alone says it is lifted). */
  rest(): void {
    this.lean = { x: 0, v: 0 };
    this.scale = { x: LIFT_SCALE, v: 0 };
  }
}

